import { useEffect, useState } from "react";
import { CloudRain, Sun, Wind, Droplets, Loader2, Thermometer, MapPin, Cloud } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const n = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString("es-PY", { maximumFractionDigits: d }));
const hora = (s) => (s ? new Date(s).toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" }) : "");

/** Clima y lluvia actuales en las coordenadas de la parcela (modelo meteorológico, caché ~45 min). */
export function useClimaAhora(cod, padron, listo = true) {
  const [c, setC] = useState(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!listo) { return undefined; }
    let vivo = true;
    let t;
    setC(null);
    api.get("/portal/titulares/parcela/clima-actual", { params: { cod, padron }, timeout: 60000 }).then((r) => { if (vivo) { setC(r.data); } }).catch((e) => {
      if (!vivo) { return; }
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Clima no disponible ahora";
      setC({ error: msg, reintentar: () => setN((x) => x + 1) });
      if (n < 2) { t = setTimeout(() => setN((x) => x + 1), 15000); }   // reintento automático (fuente gratuita ocupada)
    });
    return () => { vivo = false; clearTimeout(t); };
  }, [cod, padron, listo, n]);
  return c;
}

const Chip = ({ t, id, icon: I, children }) => <div className="ts-glass px-3 py-1.5 ts-in" data-testid={`parcela-chip-${id}`}>
  <p className="text-[9px] uppercase tracking-wider text-[#5f6f66] font-semibold">{t}</p>
  <p className="text-xs font-semibold text-[#13211b] max-w-[220px] truncate flex items-center gap-1">{I && <I className="w-3.5 h-3.5 shrink-0" />}{children}</p></div>;

/** Widgets de clima actual sobre la imagen satelital (se cargan al abrir la parcela). */
export function ClimaChips({ c, distrito }) {
  const t = (s) => (s ? String(s).toLowerCase().replace(/(^|\s)\S/g, (x) => x.toUpperCase()) : "");
  const lugar = [c?.departamento, t(distrito)].filter(Boolean).join(" · ");
  return <>
    {lugar && <Chip t="Ubicación" id="ubicacion" icon={MapPin}>{lugar}</Chip>}
    {!c ? <Chip t="Clima ahora" id="clima-ahora" icon={Loader2}>consultando…</Chip>
      : c.error ? <button onClick={c.reintentar} title={c.error} className="pointer-events-auto text-left" data-testid="parcela-clima-reintentar"><Chip t="Clima ahora" id="clima-ahora">no disponible · reintentar</Chip></button>
      : <>
        <Chip t="Clima ahora" id="clima-ahora" icon={c.lloviendo ? CloudRain : (c.nubes_pct ?? 0) >= 60 ? Cloud : Sun}>{n(c.temp_c, 1)} °C · {c.condicion}</Chip>
        <Chip t="Viento · humedad" id="clima-viento" icon={Wind}>{n(c.viento_kmh)} km/h · {n(c.humedad_pct)} %</Chip>
        {c.lluvia_24h != null && <Chip t="Lluvia 24 h / 7 d" id="clima-lluvia" icon={Droplets}>{n(c.lluvia_24h, 1)} / {n(c.lluvia_7d, 1)} mm</Chip>}
      </>}
  </>;
}

export function ClimaAhora({ c }) {
  if (!c) return <p className="text-xs text-slate-600 flex items-center gap-2" data-testid="parcela-clima-ahora-cargando"><Loader2 className="w-3.5 h-3.5 animate-spin" />Consultando clima actual…</p>;
  if (c.error) return <p className="text-xs text-rose-700" data-testid="parcela-clima-ahora-error">{c.error}</p>;
  return <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3 space-y-2" data-testid="parcela-clima-ahora">
    <div className="flex items-center gap-2">
      {c.lloviendo ? <CloudRain className="w-5 h-5 text-sky-700" /> : <Sun className="w-5 h-5 text-amber-500" />}
      <p className="text-sm font-semibold text-sky-900">Clima ahora · <span data-testid="parcela-clima-ahora-condicion">{c.condicion}</span></p>
      <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.lloviendo ? "bg-sky-600 text-white" : "bg-white text-slate-600 border border-slate-200"}`} data-testid="parcela-clima-ahora-lloviendo">{c.lloviendo ? `Lloviendo (${n(c.precip_mm, 1)} mm)` : "Sin lluvia ahora"}</span>
    </div>
    <div className="grid grid-cols-3 gap-2 text-xs">
      <p className="flex items-center gap-1" data-testid="parcela-clima-ahora-temp"><Thermometer className="w-3.5 h-3.5 text-rose-500" /><b>{n(c.temp_c, 1)} °C</b></p>
      <p className="flex items-center gap-1" data-testid="parcela-clima-ahora-humedad"><Droplets className="w-3.5 h-3.5 text-sky-600" />{n(c.humedad_pct)} %</p>
      <p className="flex items-center gap-1" data-testid="parcela-clima-ahora-viento"><Wind className="w-3.5 h-3.5 text-slate-500" />{n(c.viento_kmh)} km/h</p>
    </div>
    {c.lluvia_24h != null && <div className="grid grid-cols-3 gap-2 text-center" data-testid="parcela-clima-ahora-lluvia">
      {[["24 h", c.lluvia_24h], ["48 h", c.lluvia_48h], ["7 días", c.lluvia_7d]].map(([t, v]) => <div key={t} className="rounded-lg bg-white border border-sky-100 py-1"><p className="text-[10px] text-slate-500">Lluvia {t}</p><p className="text-sm font-bold text-sky-900">{n(v, 1)} mm</p></div>)}
    </div>}
    <p className="text-[10px] text-sky-900/80" data-testid="parcela-clima-ahora-nota">{c.nota} Fuente: {c.fuente}. Consultado {hora(c.consultado_en)}{c.desactualizado ? " · fuente sin respuesta, último dato disponible" : ""}.</p>
  </div>;
}
